import {
  ArrowLeft,
  ArrowRight,
  Inbox,
  LayoutList,
  PanelRightOpen,
  ScanLine,
  Search,
  TriangleAlert,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useMarkTutorialSeen } from '../api/auth'
import { de } from '../i18n/de'
import { primaryButton, secondaryButton } from './AddParcelDialog'

/**
 * The tour someone gets on their first signed-in page load.
 *
 * Every way out -- finishing, skipping, Escape, the backdrop, the X -- is the same exit and
 * records the tutorial as seen, because a dialog that comes back after it was dismissed is
 * worse than one that was never shown. The mark is server-side, so it holds on the next
 * device too; re-opening it from the header re-sends it, which the API treats as a no-op.
 */

/** One icon per step, in the order of `de.tutorial.steps`. Decoration, never the message. */
const ICONS = [LayoutList, Inbox, ScanLine, PanelRightOpen, Search, TriangleAlert] as const

export function Tutorial({ onClose }: { onClose: () => void }) {
  const { mutate: markSeen } = useMarkTutorialSeen()
  const [step, setStep] = useState(0)
  const dialogRef = useRef<HTMLDivElement>(null)
  const marked = useRef(false)

  const steps = de.tutorial.steps
  const current = steps[step]
  const Icon = ICONS[step] ?? LayoutList
  const isLast = step === steps.length - 1

  // The ref guards the request, not the closing: Escape, the backdrop and the X can all
  // arrive before React has unmounted us, and one POST is enough.
  const close = useCallback(() => {
    if (!marked.current) {
      marked.current = true
      markSeen()
    }
    onClose()
  }, [markSeen, onClose])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close])

  // Without this the keyboard stays on whatever opened the dialog, and Escape or Tab would
  // act on the page behind it.
  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-[10vh] backdrop-blur-sm"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl outline-none dark:bg-slate-900"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-300"
              aria-hidden
            >
              <Icon className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs font-medium text-slate-400">
                {de.tutorial.progress(step + 1, steps.length)}
              </p>
              <h2 id="tutorial-title" className="text-base font-semibold">
                {current.title}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label={de.actions.close}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          {current.body}
        </p>

        {'points' in current && current.points ? (
          <ul className="mt-3 space-y-1.5 border-l-2 border-slate-200 pl-3 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
            {current.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex items-center gap-1.5">
            {steps.map((item, index) => (
              <button
                key={item.title}
                type="button"
                onClick={() => setStep(index)}
                aria-label={de.tutorial.goTo(index + 1)}
                aria-current={index === step ? 'step' : undefined}
                className={`h-2 w-2 rounded-full transition ${
                  index === step
                    ? 'bg-indigo-600 dark:bg-indigo-400'
                    : 'bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600'
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={close}
            className="text-sm text-slate-500 transition hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
          >
            {de.tutorial.skip}
          </button>

          <div className="ml-auto flex gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((value) => value - 1)}
                className={secondaryButton}
              >
                <ArrowLeft className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                {de.tutorial.back}
              </button>
            )}
            <button
              type="button"
              onClick={() => (isLast ? close() : setStep((value) => value + 1))}
              className={primaryButton}
            >
              {isLast ? de.tutorial.done : de.tutorial.next}
              {!isLast && <ArrowRight className="ml-1 inline h-3.5 w-3.5" aria-hidden />}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
